import { isPcCanvas } from '@/utils/pc-canvas'

const DRAG_THRESHOLD = 5
const TAP_CANCEL_THRESHOLD = 12
const CLICK_SUPPRESS_MS = 400
const TAP_ONLY_TOUCH_SELECTOR = '.van-number-keyboard'
const TOUCH_COMPAT_SELECTOR = [
  '.custom-swipe-cell',
  '.index-bar',
  '.van-number-keyboard',
  '.van-picker-column',
  '[data-pc-touch-compat]',
].join(',')

interface ActivePointer {
  pointerId: number
  touchTarget: HTMLElement | null
  scrollTarget: HTMLElement | null
  clickTarget: HTMLElement
  startX: number
  startY: number
  lastY: number
  direction: 'horizontal' | 'vertical' | null
  didDrag: boolean
  tapOnly: boolean
  touchCanceled: boolean
}

let initialized = false
let activePointer: ActivePointer | null = null
let suppressClickUntil = 0
let suppressedClickTarget: HTMLElement | null = null

const isEditable = (target: HTMLElement): boolean => (
  target instanceof HTMLInputElement
  || target instanceof HTMLTextAreaElement
  || target instanceof HTMLSelectElement
  || target.isContentEditable
)

const findScrollableAncestor = (target: HTMLElement): HTMLElement | null => {
  if (isEditable(target)) return null

  let current: HTMLElement | null = target
  while (current && current !== document.body) {
    const { overflowY } = window.getComputedStyle(current)
    if (
      (overflowY === 'auto' || overflowY === 'scroll')
      && current.scrollHeight > current.clientHeight + 1
    ) {
      return current
    }
    current = current.parentElement
  }

  const scrollingElement = document.scrollingElement
  if (
    scrollingElement instanceof HTMLElement
    && scrollingElement.scrollHeight > scrollingElement.clientHeight + 1
  ) {
    return scrollingElement
  }
  return null
}

const createTouch = (event: PointerEvent, target: HTMLElement): Touch => {
  const init: TouchInit = {
    identifier: event.pointerId,
    target,
    clientX: event.clientX,
    clientY: event.clientY,
    pageX: event.pageX,
    pageY: event.pageY,
    screenX: event.screenX,
    screenY: event.screenY,
    radiusX: 1,
    radiusY: 1,
    rotationAngle: 0,
    force: event.pressure || 0.5,
  }

  if (typeof Touch === 'function') return new Touch(init)
  return init as unknown as Touch
}

const dispatchTouch = (
  type: 'touchstart' | 'touchmove' | 'touchend' | 'touchcancel',
  pointerEvent: PointerEvent,
  target: HTMLElement,
): boolean => {
  const touch = createTouch(pointerEvent, target)
  const isEnd = type === 'touchend' || type === 'touchcancel'
  const touches = isEnd ? [] : [touch]
  const changedTouches = [touch]
  let event: Event

  if (typeof TouchEvent === 'function') {
    event = new TouchEvent(type, {
      bubbles: true,
      cancelable: true,
      composed: true,
      touches,
      targetTouches: touches,
      changedTouches,
    })
  } else {
    event = new Event(type, { bubbles: true, cancelable: true, composed: true })
    Object.defineProperties(event, {
      touches: { value: touches },
      targetTouches: { value: touches },
      changedTouches: { value: changedTouches },
    })
  }

  return target.dispatchEvent(event)
}

const handlePointerDown = (event: PointerEvent) => {
  if (!isPcCanvas() || event.pointerType !== 'mouse' || !event.isPrimary || event.button !== 0) return
  if (!(event.target instanceof Element)) return
  const target = event.target instanceof HTMLElement ? event.target : event.target.parentElement
  if (!target) return

  const touchSurface = target.closest<HTMLElement>(TOUCH_COMPAT_SELECTOR)
  const scrollTarget = touchSurface?.matches('.van-number-keyboard, .van-picker-column')
    ? null
    : findScrollableAncestor(target)
  const clickTarget = touchSurface || scrollTarget
  if (!clickTarget) return

  activePointer = {
    pointerId: event.pointerId,
    touchTarget: touchSurface ? target : null,
    scrollTarget,
    clickTarget,
    startX: event.clientX,
    startY: event.clientY,
    lastY: event.clientY,
    direction: null,
    didDrag: false,
    tapOnly: touchSurface?.matches(TAP_ONLY_TOUCH_SELECTOR) ?? false,
    touchCanceled: false,
  }

  if (touchSurface) dispatchTouch('touchstart', event, target)
}

const handlePointerMove = (event: PointerEvent) => {
  const state = activePointer
  if (!state || state.pointerId !== event.pointerId) return

  const deltaX = event.clientX - state.startX
  const deltaY = event.clientY - state.startY
  const distance = Math.max(Math.abs(deltaX), Math.abs(deltaY))
  if (!state.direction && Math.max(Math.abs(deltaX), Math.abs(deltaY)) >= DRAG_THRESHOLD) {
    state.direction = Math.abs(deltaX) > Math.abs(deltaY) ? 'horizontal' : 'vertical'
  }

  let touchHandled = true
  if (state.touchTarget && state.tapOnly && distance >= TAP_CANCEL_THRESHOLD && !state.touchCanceled) {
    dispatchTouch('touchmove', event, state.touchTarget)
    state.touchCanceled = true
    state.didDrag = true
    event.preventDefault()
  } else if (state.touchTarget && !state.tapOnly) {
    touchHandled = dispatchTouch('touchmove', event, state.touchTarget)
  }

  const scrollTarget = state.direction === 'vertical' ? state.scrollTarget : null
  if (scrollTarget) scrollTarget.scrollTop -= event.clientY - state.lastY
  state.lastY = event.clientY

  if (state.direction && ((state.touchTarget && !state.tapOnly) || scrollTarget)) {
    state.didDrag = true
    event.preventDefault()
  } else if (!touchHandled) {
    event.preventDefault()
  }
}

const finishPointer = (type: 'touchend' | 'touchcancel', event: PointerEvent) => {
  const state = activePointer
  if (!state || state.pointerId !== event.pointerId) return

  if (state.touchTarget) dispatchTouch(type, event, state.touchTarget)
  if (state.didDrag || state.clickTarget.matches('.index-bar, .van-number-keyboard')) {
    suppressClickUntil = performance.now() + CLICK_SUPPRESS_MS
    suppressedClickTarget = state.clickTarget
  }
  activePointer = null
}

const handleClick = (event: MouseEvent) => {
  if (performance.now() >= suppressClickUntil) {
    suppressedClickTarget = null
    return
  }
  if (!suppressedClickTarget || !event.composedPath().includes(suppressedClickTarget)) return
  suppressClickUntil = 0
  suppressedClickTarget = null
  event.preventDefault()
  event.stopImmediatePropagation()
}

/** Desktop-only adapter for controls that expose touch gestures but no mouse path. */
export const setupPcPointerCompatibility = (): void => {
  if (initialized || typeof document === 'undefined' || !isPcCanvas()) return
  initialized = true

  document.addEventListener('pointerdown', handlePointerDown, true)
  document.addEventListener('pointermove', handlePointerMove, { capture: true, passive: false })
  document.addEventListener('pointerup', (event) => finishPointer('touchend', event), true)
  document.addEventListener('pointercancel', (event) => finishPointer('touchcancel', event), true)
  document.addEventListener('click', handleClick, true)
}
